import React, { useMemo, useCallback } from "react";
import { Modal } from "react-bootstrap";
import MovieCard from "./MovieCard";
import "./MovieGridExperiment.css";

const formatVotes = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(num);
};

const displayTitle = (movie) => movie.title || movie.name || "Untitled";

const yearFromMovie = (movie) => {
  const d = movie.release_date || movie.first_air_date;
  if (!d) return "";
  const y = new Date(d).getFullYear();
  return Number.isNaN(y) ? "" : String(y);
};

/**
 * Below-lg experiment: 2-column poster grid; full MovieCard opens in a modal overlay.
 */
const MovieGridExperiment = ({
  movies,
  expandedMovieKey,
  onToggle,
  movieKey,
  handleShowTrailer,
  selectedProvider,
}) => {
  const expandedMovie = useMemo(
    () => movies.find((m) => movieKey(m) === expandedMovieKey) || null,
    [movies, expandedMovieKey, movieKey]
  );

  const closeDetail = useCallback(() => {
    if (expandedMovie) onToggle(expandedMovie);
  }, [expandedMovie, onToggle]);

  return (
    <div className="movie-grid-experiment">
      <div className="movie-grid-experiment__grid">
        {movies.map((movie) => {
          const key = movieKey(movie);
          const expanded = expandedMovieKey === key;
          const title = displayTitle(movie);
          const year = yearFromMovie(movie);
          const poster = movie.poster_path
            ? `https://image.tmdb.org/t/p/w500${movie.poster_path}`
            : null;
          const rating =
            movie.vote_average != null && !Number.isNaN(Number(movie.vote_average))
              ? Number(movie.vote_average).toFixed(1)
              : "—";
          const votes = formatVotes(movie.vote_count || 0);

          const { castShort, castTitle } = (() => {
            if (!movie.cast || !movie.cast.length) {
              return { castShort: null, castTitle: null };
            }
            const names = movie.cast.filter(Boolean);
            const titleAttr = names.join(", ");
            const short =
              names.slice(0, 4).join(", ") + (names.length > 4 ? "…" : "");
            return { castShort: short, castTitle: titleAttr };
          })();

          const label = expanded
            ? `Close details for ${title}`
            : `Open details for ${title}${castTitle ? `. Cast: ${castTitle}` : ""}`;

          return (
            <button
              key={key}
              type="button"
              className={`movie-grid-experiment__thumb-btn${expanded ? " movie-grid-experiment__thumb-btn--selected" : ""}`}
              onClick={() => onToggle(movie)}
              aria-expanded={expanded}
              aria-haspopup="dialog"
              aria-controls="movie-grid-detail-modal"
              id={`movie-grid-thumb-${movie.id}`}
              aria-label={label}
            >
              <div className="movie-grid-experiment__poster-wrap">
                {poster ? (
                  <img src={poster} alt="" loading="lazy" decoding="async" />
                ) : (
                  <div className="movie-grid-experiment__poster-placeholder" aria-hidden>
                    🎬
                  </div>
                )}
              </div>
              <div className="movie-grid-experiment__meta">
                <div className="movie-grid-experiment__title">
                  {title}
                  {year ? ` (${year})` : ""}
                </div>
                <div className="movie-grid-experiment__row">
                  <span className="movie-grid-experiment__rating">
                    <span className="movie-grid-experiment__star" aria-hidden>
                      ⭐
                    </span>
                    <span className="movie-grid-experiment__rating-num">{rating}</span>
                  </span>
                  <span className="movie-grid-experiment__votes">{votes} votes</span>
                </div>
                {castShort && (
                  <div className="movie-grid-experiment__cast" title={castTitle}>
                    <span className="movie-grid-experiment__cast-label">Cast:</span>
                    {castShort}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <Modal
        id="movie-grid-detail-modal"
        show={Boolean(expandedMovie)}
        onHide={closeDetail}
        fullscreen="lg-down"
        scrollable
        centered
        className="movie-grid-detail-modal"
        contentClassName="movie-grid-detail-modal__content"
        aria-labelledby="movie-grid-detail-modal-title"
      >
        <Modal.Header closeButton className="movie-grid-detail-modal__header">
          <Modal.Title className="h6 mb-0 text-truncate" id="movie-grid-detail-modal-title">
            {expandedMovie ? displayTitle(expandedMovie) : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="movie-grid-detail-modal__body">
          {expandedMovie && (
            <MovieCard
              movie={expandedMovie}
              handleShowTrailer={handleShowTrailer}
              selectedProvider={selectedProvider}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MovieGridExperiment;
